import { useEffect } from 'react';
import cn from 'classnames';
import dayjs from 'dayjs';
// Helpers
import {
  DateSpecificHours,
  createDateTimeObject,
  DATEPICKER_DATE_FORMAT,
  getPickerValue,
} from '../helpers';
// UI
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
import Input from 'ui3/Input/Input';
import DatePicker from 'ui3/DatePicker/DatePicker';
import Text from 'ui3/Text/Text';
import Button from 'ui3/Button/Button';
// Styles
import styles from './DateSpecificHoursForm.module.scss';

type DateSpecificHoursFormProps = {
  specificHoursList: DateSpecificHours[];
  setSpecificHoursList: (value: DateSpecificHours[]) => void;
  onValidationChange: (isValid: boolean) => void;
};

const DateSpecificHoursForm = ({
  specificHoursList,
  setSpecificHoursList,
  onValidationChange,
}: DateSpecificHoursFormProps): JSX.Element => {
  const addDayRange = () => {
    setSpecificHoursList([
      ...specificHoursList,
      {
        date: null,
        timeRanges: [{ from: null, to: null }],
      },
    ]);
  };

  const handleRemoveDay = (dayIndex: number) => {
    const newSpecificHoursList = specificHoursList.filter(
      (_, counter) => counter !== dayIndex
    );

    setSpecificHoursList(newSpecificHoursList);
  };

  const handleDateChange = (
    value: Date | [Date, Date] | null,
    index: number
  ) => {
    const newSpecificHoursList = [...specificHoursList];
    newSpecificHoursList[index].date = getPickerValue(value);

    setSpecificHoursList(newSpecificHoursList);
  };

  const handleTimeChange = (
    value: [dayjs.Dayjs | null, dayjs.Dayjs | null],
    dayIndex: number,
    timeIndex: number
  ) => {
    const [from, to] = value || [null, null];

    const newSpecificHoursList = [...specificHoursList];
    newSpecificHoursList[dayIndex].timeRanges[timeIndex] = {
      from,
      to,
    };

    setSpecificHoursList(newSpecificHoursList);
  };

  const handleAddTimeRange = (index: number) => {
    const newSpecificHoursList = [...specificHoursList];
    newSpecificHoursList[index].timeRanges.push({ from: null, to: null });

    setSpecificHoursList(newSpecificHoursList);
  };

  const handleRemoveTimeRange = (dayIndex: number, timeIndex: number) => {
    const newTimeRanges = specificHoursList[dayIndex].timeRanges.filter(
      (_, counter) => counter !== timeIndex
    );
    const newSpecificHoursList = [...specificHoursList];
    newSpecificHoursList[dayIndex].timeRanges = newTimeRanges;

    setSpecificHoursList(newSpecificHoursList);
  };

  useEffect(() => {
    // No error if we have an empty schedule
    if (specificHoursList.length === 0) {
      onValidationChange(true);
      return;
    }

    // Validate that each date entry has a date and valid time ranges
    const isValid = specificHoursList.every((entry) => {
      const hasDate = Boolean(entry.date);
      const hasTimeIntervals = entry.timeRanges.every(
        (range) => range.from !== null && range.to !== null
      );

      return hasDate && hasTimeIntervals;
    });

    onValidationChange(isValid);
  }, [specificHoursList, onValidationChange]);

  return (
    <div className={styles.root}>
      <div className={styles.titleBlock}>
        <Text variant="subtitle2" color="lights-high">
          Date-specific Hours
        </Text>
        <Text variant="body2Regular" color="lights-low">
          Set specific hours for individual dates when your availability differs
          from your regular schedule.
        </Text>
      </div>
      <div className={styles.dateWrapper}>
        {specificHoursList.map((day, dayIndex) => {
          const lastItem = day.timeRanges.length - 1;

          const selectedDate = createDateTimeObject(day.date);

          return (
            <div key={'day_' + dayIndex} className={styles.dayWrapper}>
              <div className={styles.dayPickerWrapper}>
                <DatePicker
                  showYearDropdown
                  showMonthDropdown
                  onChange={(e) => handleDateChange(e, dayIndex)}
                  minDate={new Date()}
                  selected={selectedDate}
                  dateFormat={DATEPICKER_DATE_FORMAT}
                  placeholderText="Select date"
                  portalId="date-specific-hours-picker-portal"
                  popperClassName={styles.datePickerPopper}
                  customInput={
                    <Input
                      className={cn(styles.timeInput, {
                        [styles.errorInput]: !day.date,
                      })}
                      name={'dayInput_' + dayIndex}
                      inputMode="none"
                      endIcons={[{ name: 'calendar' }]}
                    />
                  }
                />
              </div>
              <div className={styles.timePickerWrapper}>
                {day.timeRanges?.map((timeRange, timeIndex) => {
                  const timeFrom = createDateTimeObject(timeRange.from);
                  const timeTo = createDateTimeObject(timeRange.to);

                  const minTime = timeFrom
                    ? dayjs(dayjs(timeFrom).add(15, 'minutes')).toDate()
                    : undefined;

                  const maxTime = dayjs().endOf('day').toDate();

                  return (
                    <div
                      key={'time_' + timeIndex}
                      className={styles.timeRangeWrapper}
                    >
                      <div className={styles.timeRange}>
                        <DatePicker
                          className={cn(styles.timeInput, {
                            [styles.errorInput]: !timeRange.from,
                          })}
                          showTimeSelect
                          showTimeSelectOnly
                          timeIntervals={15}
                          timeCaption="Time"
                          dateFormat="h:mm aa"
                          selected={timeFrom}
                          onChange={(e) =>
                            handleTimeChange(
                              [getPickerValue(e), timeRange.to],
                              dayIndex,
                              timeIndex
                            )
                          }
                          placeholderText="Start time"
                          portalId="date-specific-hours-picker-portal"
                          popperClassName={styles.datePickerPopper}
                          customInput={
                            <Input
                              className={styles.timeInput}
                              name={`date_${dayIndex}_time_${timeIndex}_from`}
                              inputMode="none"
                            />
                          }
                        />
                        <p className={styles.timeSeparator}>to</p>
                        <DatePicker
                          className={styles.timeInput}
                          showTimeSelect
                          showTimeSelectOnly
                          timeIntervals={15}
                          timeCaption="Time"
                          dateFormat="h:mm aa"
                          selected={timeTo}
                          onChange={(e) =>
                            handleTimeChange(
                              [timeRange.from, getPickerValue(e)],
                              dayIndex,
                              timeIndex
                            )
                          }
                          placeholderText="End time"
                          minTime={minTime}
                          maxTime={maxTime}
                          disabled={!(minTime && maxTime)}
                          portalId="date-specific-hours-picker-portal"
                          popperClassName={styles.datePickerPopper}
                          customInput={
                            <Input
                              className={cn(styles.timeInput, {
                                [styles.errorInput]: !timeRange.to,
                              })}
                              name={`date_${dayIndex}_time_${timeIndex}_to`}
                              inputMode="none"
                            />
                          }
                        />
                      </div>
                      <div className={styles.iconWrapper}>
                        {timeIndex === lastItem && (
                          <button
                            type="button"
                            className={styles.iconButton}
                            aria-label={`Add time range for date ${
                              dayIndex + 1
                            }`}
                            onClick={() => handleAddTimeRange(dayIndex)}
                          >
                            <MillionsIcon
                              className={styles.icon}
                              name="plusCircleOutline"
                            />
                          </button>
                        )}
                        {timeIndex > 0 && (
                          <button
                            type="button"
                            className={styles.iconButton}
                            aria-label={`Remove time range ${
                              timeIndex + 1
                            } for date ${dayIndex + 1}`}
                            onClick={() =>
                              handleRemoveTimeRange(dayIndex, timeIndex)
                            }
                          >
                            <MillionsIcon
                              className={styles.icon}
                              name="minusCircle"
                            />
                          </button>
                        )}
                        {timeIndex === 0 && timeIndex === lastItem && (
                          <button
                            type="button"
                            className={styles.iconButton}
                            aria-label={`Remove date ${dayIndex + 1}`}
                            onClick={() => handleRemoveDay(dayIndex)}
                          >
                            <MillionsIcon
                              className={styles.icon}
                              name="minusCircle"
                            />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        <div>
          <Button
            type="button"
            variant="primary"
            subVariant="text"
            icon="plusCircleOutline"
            className={styles.addDayButton}
            onClick={() => addDayRange()}
          >
            Add date specific hours
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DateSpecificHoursForm;
